import { Component, computed, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { take } from 'rxjs';
import { BeaconQueryFilterDto } from '../../dto/beacon-query-filter.dto';
import { BeaconFiltersComponent } from "../beacon-filters/beacon-filters.component";
import { BeaconService } from '../../service/beacon.service';
import { BeaconProjectionDto } from '../../dto/beacon-projection.dto';
import { BeaconTableComponent } from "../beacon-table/beacon-table.component";
import { MapComponent, MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { Params } from '@angular/router';
import { HasRoleDirective } from '/app/shared/directives/has-role.directive';

@Component({
  selector: 'app-beacon-home',
  imports: [BeaconFiltersComponent, BeaconTableComponent, MapComponent, MarkerComponent, PopupComponent, RouterLink, HasRoleDirective],
  templateUrl: './beacon-home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './beacon-home.component.scss',
})
export class BeaconHomeComponent implements OnInit {
  currentFilters = signal<BeaconQueryFilterDto>({ page: 0, pageSize: 100 });
  beacons = signal<BeaconProjectionDto[]>([]);

  isLoading = signal(false);

  totalRecords = signal(0);
  first = signal(0);
  rows = signal(100);

  statusMessage = signal<{ text: string; type: 'success' | 'error' } | null>(null);

  selectedBeaconId = signal<string | null>(null);
  mapBeacons = computed(() => {
    const all = this.beacons();
    const selected = this.selectedBeaconId();
    if (selected) {
      return all.filter(b => b.id === selected);
    }
    return all;
  });

  private _mapInstance: MapLibreMap | null = null;

  constructor(
    private _beaconService: BeaconService,
    private _route: ActivatedRoute,
    private _router: Router,
  ) {}

  onFiltersApplied(filters: BeaconQueryFilterDto): void {
    this.selectedBeaconId.set(null);
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v != null && !(typeof v === 'number' && isNaN(v)))
    ) as BeaconQueryFilterDto;
    const hasPagination = cleaned.page !== undefined && cleaned.pageSize !== undefined;
    const base = hasPagination ? this.currentFilters() : { page: 0, pageSize: 100 };
    const merged = { ...base, ...cleaned } as BeaconQueryFilterDto;
    this.currentFilters.set(merged);
    this.rows.set(100);
    this.first.set((merged.page ?? 0) * (merged.pageSize ?? 100));
    this._updateUrlParams(merged);
    this._fetchBeacons(merged);
  }

  ngOnInit(): void {
    this._route.queryParams.pipe(take(1)).subscribe(params => {
      const filters = this._buildFiltersFromParams(params);
      this.currentFilters.set(filters);
      this._fetchBeacons(filters);
    });
  }

  onMapLoad(map: MapLibreMap): void {
    this._mapInstance = map;
  }

  onSelectedBeacon(beacon: BeaconProjectionDto): void {
    this.selectedBeaconId.set(beacon.id);
    if (this._mapInstance && beacon.latitude != null && beacon.longitude != null) {
      this._mapInstance.flyTo({
        center: [beacon.longitude, beacon.latitude],
        zoom: 14,
      });
    }
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleString();
  }

  timeWithoutReporting(beacon: BeaconProjectionDto): string {
    const now = new Date();
    const reportDate = new Date(beacon.reportDate);
    const diffMs = now.getTime() - reportDate.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  }

  private _showMessage(text: string, type: 'success' | 'error'): void {
    this.statusMessage.set({ text, type });
    setTimeout(() => this.statusMessage.set(null), 5000);
  }

  private _buildFiltersFromParams(params: Params): BeaconQueryFilterDto {
    return {
      page: params['page'] !== undefined ? Number(params['page']) : 0,
      pageSize: params['page-size'] !== undefined ? Number(params['page-size']) : 100,
      search: params['search'] || undefined,
      corporateId: params['corporate-id'] || undefined,
      groupId: params['group-id'] || undefined,
      sortBy: params['beacon-sort-by'] !== undefined ? Number(params['beacon-sort-by']) : undefined,
      sortOrder: params['sort-order'] !== undefined ? Number(params['sort-order']) : undefined,
      trackingEnabled: params['tracking-enabled'] !== undefined ? params['tracking-enabled'] === 'true' : undefined,
    };
  }

  private _updateUrlParams(filters: BeaconQueryFilterDto): void {
    const queryParams: Record<string, string> = {};
    if (filters.page != null) queryParams['page'] = String(filters.page);
    if (filters.pageSize != null) queryParams['page-size'] = String(filters.pageSize);
    if (filters.search) queryParams['search'] = filters.search;
    if (filters.corporateId) queryParams['corporate-id'] = filters.corporateId;
    if (filters.groupId) queryParams['group-id'] = filters.groupId;
    if (filters.sortBy != null) queryParams['beacon-sort-by'] = String(filters.sortBy);
    if (filters.sortOrder != null) queryParams['sort-order'] = String(filters.sortOrder);
    if (filters.trackingEnabled != null) queryParams['tracking-enabled'] = String(filters.trackingEnabled);

    this._router.navigate([], {
      relativeTo: this._route,
      queryParams,
      replaceUrl: true,
    });
  }

  onBeaconUpdated() {
    this.onFiltersApplied(this.currentFilters());
  }

  private _fetchBeacons(filters: BeaconQueryFilterDto): void {
    console.log("fetching", filters);
    this.isLoading.set(true);

    this._beaconService.getBeacons(filters).subscribe({
      next: (response) => {
        if(response.success && response.data) {
          this._showMessage(`Fetched ${response.data.length} beacons.`, 'success');
          const beacons = response.data;
          this.beacons.set(beacons);

          const pagination = response.meta?.pagination;
          if (pagination) {
            const page = pagination.page ?? 0;
            const pageSize = pagination.pageSize ?? this.currentFilters().pageSize ?? 100;
            this.totalRecords.set(pagination.totalItems ?? 0);
            this.first.set(page * pageSize);
            this.rows.set(pageSize);
          }

        }
        this.isLoading.set(false);
      },
      error: (error) => {
        this.isLoading.set(false);
        this._showMessage(`Error fetching beacons: ${error.message}`, 'error');
      },
    });
  }
}
